import { memo, useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';

import { useTheme } from 'context/theme';
import { useClampText } from 'hooks/useClampText';
import { StyledComponent } from 'types';
import { truncate } from 'utils/truncate';

export const ExpandableTextTestIds = {
  container: 'expandable-container'
};

export type ExpandableTextProps = {
  text?: string;
  collapsedCharacterCount?: number;
  lines?: number;
  onClickMore?: () => void;
  forcedCollapseState?: boolean;
  moreButtonClassName?: string;
  moreButtonContainerClassName?: string;
} & StyledComponent;

export const ExpandableText = memo(
  ({
    text,
    className,
    collapsedCharacterCount = 256,
    lines,
    onClickMore,
    forcedCollapseState,
    moreButtonContainerClassName,
    moreButtonClassName = ''
  }: ExpandableTextProps) => {
    const [collapsed, setCollapsed] = useState(true);
    const textCollapsable = text && text.length > collapsedCharacterCount;
    const { isDark } = useTheme();
    const moreLessButtonClassname = `p-0 ${isDark ? 'text-light' : 'text-primary'} riforma-medium`;

    const onExpand = useCallback(() => (onClickMore ? onClickMore() : setCollapsed(false)), [onClickMore]);

    useEffect(() => {
      if (!forcedCollapseState) {
        return;
      }
      setCollapsed(forcedCollapseState);
    }, [forcedCollapseState]);

    const clamped = useClampText({
      lines,
      text,
      ellipsisClassName: moreButtonContainerClassName,
      ellipsis: (
        <span>
          <span>... </span>
          <span role='button' className={classNames(moreLessButtonClassname, moreButtonClassName)} onClick={onExpand}>
            MORE
          </span>
        </span>
      )
    });

    if (lines && collapsed) {
      return <div className={className}>{clamped}</div>;
    }

    if (collapsed) {
      const collapsedText = truncate(text, collapsedCharacterCount);

      return (
        <p data-testid={ExpandableTextTestIds.container} style={{ whiteSpace: 'pre-wrap' }} className={className}>
          {collapsedText}{' '}
          {textCollapsable && (
            <span role='button' className={classNames(moreLessButtonClassname, moreButtonClassName)} onClick={onExpand}>
              MORE
            </span>
          )}
        </p>
      );
    }

    return (
      <p data-testid={ExpandableTextTestIds.container} style={{ whiteSpace: 'pre-wrap' }} className={className}>
        {text}{' '}
        <span
          role='button'
          className={classNames(moreLessButtonClassname, moreButtonClassName)}
          onClick={() => setCollapsed(true)}
        >
          LESS
        </span>
      </p>
    );
  }
);
